import {
  PortalBriefResponseSchema,
  PortalCommentCreateSchema,
  PortalCommentListResponseSchema,
  PortalInboxCompleteUploadSchema,
  PortalInboxUploadRequestSchema,
  PortalInboxUploadTokenResponseSchema,
  PortalMessageCreateSchema,
  PortalMessageListResponseSchema,
  PortalThreadListResponseSchema,
  type PortalComment,
  type PortalCommentCreate,
  type PortalInboxCompleteUpload,
  type PortalInboxUploadRequest,
  type PortalMessage,
  type PortalMessageCreate,
} from "@l30/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { portalFetch } from "./portal-client.js";

const EXPERIENCE_KEY = ["portal-experience"] as const;

export function usePortalBrief() {
  return useQuery({
    queryKey: [...EXPERIENCE_KEY, "brief"],
    queryFn: () => portalFetch("/portal/brief", {}, PortalBriefResponseSchema),
  });
}

export function usePortalThreads() {
  return useQuery({
    queryKey: [...EXPERIENCE_KEY, "threads"],
    queryFn: () => portalFetch("/portal/threads", {}, PortalThreadListResponseSchema),
  });
}

export function usePortalThreadMessages(threadId: string | null) {
  return useQuery({
    queryKey: [...EXPERIENCE_KEY, "messages", threadId],
    enabled: !!threadId,
    queryFn: () =>
      portalFetch(`/portal/threads/${threadId}/messages`, {}, PortalMessageListResponseSchema),
  });
}

export function useCreatePortalMessage(threadId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PortalMessageCreate) => {
      if (!threadId) throw new Error("Thread required");
      const parsed = PortalMessageCreateSchema.parse(data);
      return portalFetch<PortalMessage>(`/portal/threads/${threadId}/messages`, {
        method: "POST",
        body: JSON.stringify(parsed),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: EXPERIENCE_KEY }),
  });
}

export function usePortalComments(targetKind: string | null, targetId: string | null) {
  return useQuery({
    queryKey: [...EXPERIENCE_KEY, "comments", targetKind, targetId],
    enabled: !!targetKind && !!targetId,
    queryFn: () =>
      portalFetch(
        `/portal/comments?targetKind=${encodeURIComponent(targetKind!)}&targetId=${encodeURIComponent(targetId!)}`,
        {},
        PortalCommentListResponseSchema,
      ),
  });
}

export function useCreatePortalComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PortalCommentCreate) => {
      const parsed = PortalCommentCreateSchema.parse(data);
      return portalFetch<PortalComment>("/portal/comments", {
        method: "POST",
        body: JSON.stringify(parsed),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: EXPERIENCE_KEY }),
  });
}

export function usePortalInboxUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, note }: { file: File; note?: string }) => {
      const request: PortalInboxUploadRequest = PortalInboxUploadRequestSchema.parse({
        filename: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        ...(note ? { note } : {}),
      });

      const token = await portalFetch(
        "/portal/inbox/upload-token",
        {
          method: "POST",
          body: JSON.stringify(request),
        },
        PortalInboxUploadTokenResponseSchema,
      );

      const upload = await fetch(token.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": request.mimeType,
          "x-ms-blob-type": "BlockBlob",
        },
        body: file,
      });

      if (!upload.ok) {
        throw new Error(`Upload failed: ${upload.status}`);
      }

      const completeBody: PortalInboxCompleteUpload = PortalInboxCompleteUploadSchema.parse({
        itemId: token.item.id,
        ...(note ? { note } : {}),
      });

      return portalFetch("/portal/inbox/complete", {
        method: "POST",
        body: JSON.stringify(completeBody),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: EXPERIENCE_KEY }),
  });
}
